/**
 * StickyBrief - World-Class Case Summary Panel
 * 
 * Design: ChatGPT-inspired, ultra clean, premium feel
 * - Expanded by default - shows full case intelligence
 * - Auto-collapses smoothly when user starts chatting
 * - Beautiful animations and typography
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  ChevronDown, ChevronUp, RefreshCw, Sparkles, Clock, AlertTriangle, Loader2,
  FileText, ExternalLink, Mail, CheckCircle, ArrowRight, Download,
  Target, Shield, Calendar, Lightbulb, Scale, Zap
} from 'lucide-react';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { cn } from '../../lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { enUS, nb } from 'date-fns/locale';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface StickyBriefProps {
  caseId: any;
  caseName: any;
  shouldCollapse?: boolean;
}

const StickyBrief: React.FC<StickyBriefProps> = ({ caseId, caseName, shouldCollapse = false }) => {
  const { t, i18n } = useTranslation(['workspace', 'common']);
  const dateLocale = i18n.language === 'nb' ? nb : enUS;
  
  // Start expanded, collapse when chat starts
  const [isExpanded, setIsExpanded] = useState(true);
  const hasCollapsedOnce = useRef(false);
  
  // Auto-collapse with smooth animation when chat starts
  useEffect(() => {
    if (shouldCollapse && !hasCollapsedOnce.current) {
      // Small delay for smooth UX
      const timer = setTimeout(() => {
        setIsExpanded(false);
        hasCollapsedOnce.current = true;
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [shouldCollapse]);
  const [brief, setBrief] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasCached, setHasCached] = useState(false);
  const [briefAge, setBriefAge] = useState(null);
  const [primaryDocument, setPrimaryDocument] = useState(null);
  const [loadingDocuments, setLoadingDocuments] = useState(true);

  // Fetch case documents
  const fetchCaseDocuments = useCallback(async () => {
    if (!caseId) return;
    try {
      const token = auth.getToken();
      const response = await axios.get(`${API}/cases/${caseId}/documents`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      const docs = response.data.documents || [];
      if (docs.length > 0) {
        const docxFiles = docs.filter(d => d.filename?.toLowerCase().endsWith('.docx'));
        const contractFiles = docs.filter(d => {
          const name = d.filename?.toLowerCase() || '';
          return name.includes('avtale') || name.includes('kontrakt') || name.includes('contract');
        });
        setPrimaryDocument(contractFiles[0] || docxFiles[0] || docs[0]);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoadingDocuments(false);
    }
  }, [caseId]);

  // Fetch cached brief
  const fetchCachedBrief = useCallback(async () => {
    if (!caseId) return;
    const lang = i18n.language === 'nb' ? 'no' : 'en';
    try {
      const token = auth.getToken();
      const response = await axios.get(`${API}/ai-workspace/cases/${caseId}/brief/cached`, { 
        headers: { Authorization: `Bearer ${token}` },
        params: { lang }
      });
      if (response.data.has_cached && response.data.brief) {
        setBrief(response.data.brief);
        setHasCached(true);
        setBriefAge(response.data.age_hours);
      } else {
        await generateNewBrief();
      }
    } catch (error) {
      await generateNewBrief();
    } finally {
      setIsLoading(false);
    }
  }, [caseId, i18n.language]);

  // Generate new brief
  const generateNewBrief = async () => {
    if (!caseId) return;
    setIsRefreshing(true);
    const lang = i18n.language === 'nb' ? 'no' : 'en';
    try {
      const token = auth.getToken();
      const response = await fetch(`${API}/ai-workspace/cases/${caseId}/brief/stream?lang=${lang}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.content) fullContent += data.content;
              if (data.done) {
                const cleanContent = fullContent.replace(/\*\*/g, '');
                setBrief({
                  content: cleanContent,
                  summary_line: cleanContent.split('\n')[0]?.slice(0, 100),
                  generated_at: new Date().toISOString()
                });
                setHasCached(true);
                setBriefAge(0);
              }
            } catch (e) {}
          }
        }
      }
    } catch (error) {
      console.error('Failed to generate brief:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDownloadDocument = async () => {
    if (!primaryDocument) return;
    try {
      const token = auth.getToken();
      const response = await axios.get(`${API}/documents/${primaryDocument.id}/download-for-edit`, { 
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = primaryDocument.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Document downloaded');
    } catch (error) {
      toast.error('Could not download document');
    }
  };

  useEffect(() => {
    fetchCachedBrief();
    fetchCaseDocuments();
  }, [fetchCachedBrief, fetchCaseDocuments]);

  // Parse brief content into sections
  const parseBriefContent = (content) => {
    if (!content) return null;
    
    // Remove all ** symbols and "Core of the Case" prefixes globally
    let cleanContent = content.replace(/\*\*/g, '');
    // Remove various forms of "Core of the Case" header
    cleanContent = cleanContent.replace(/Core of the Case:?\s*/gi, '');
    cleanContent = cleanContent.replace(/Kjernen i saken:?\s*/gi, '');
    cleanContent = cleanContent.replace(/Case Summary:?\s*/gi, '');
    cleanContent = cleanContent.replace(/Saksoversikt:?\s*/gi, '');
    
    const sections = {
      summary: '',
      keyPoints: [],
      risks: [],
      nextSteps: [],
      deadlines: []
    };

    // Simple parsing - extract meaningful content
    const lines = cleanContent.split('\n').filter(l => l.trim());
    let currentSection = 'summary';
    
    lines.forEach(line => {
      let cleanLine = line.replace(/^#+\s*/, '').trim();
      // Also clean from individual lines
      cleanLine = cleanLine.replace(/^Core of the Case:?\s*/i, '');
      cleanLine = cleanLine.replace(/^Kjernen i saken:?\s*/i, '');
      
      if (line.includes('Key') || line.includes('Viktig') || line.includes('Hovedpunkt')) {
        currentSection = 'keyPoints';
      } else if (line.includes('Risk') || line.includes('Risiko') || line.includes('Warning')) {
        currentSection = 'risks';
      } else if (line.includes('Next') || line.includes('Neste') || line.includes('Action')) {
        currentSection = 'nextSteps';
      } else if (line.includes('Deadline') || line.includes('Frist') || line.includes('Date')) {
        currentSection = 'deadlines';
      } else if (cleanLine && !line.startsWith('#')) {
        if (currentSection === 'summary' && !sections.summary) {
          sections.summary = cleanLine;
        } else if (line.startsWith('-') || line.startsWith('•') || line.startsWith('*')) {
          const item = cleanLine.replace(/^[-•*]\s*/, '');
          if (item) sections[currentSection]?.push?.(item);
        }
      }
    });

    return sections;
  };

  const parsedBrief = brief ? parseBriefContent(brief.content) : null;

  // Loading state
  if (isLoading) {
    return (
      <div className="border-b border-gray-100 bg-white px-6 py-3">
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Loading case intelligence...</span>
        </div>
      </div>
    );
  }

  // No brief
  if (!brief && !isRefreshing) {
    return (
      <div className="border-b border-gray-100 bg-white px-6 py-3">
        <button
          onClick={generateNewBrief}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
        >
          <Sparkles className="w-4 h-4" />
          <span>Generate case brief</span>
        </button>
      </div>
    );
  }

  return (
    <div className={cn(
      "bg-white rounded-2xl border border-gray-100 transition-all duration-500 ease-out overflow-hidden",
      isExpanded ? "shadow-sm" : "hover:shadow-sm"
    )}>
      {/* === HEADER - Always visible === */}
      <div 
        className={cn(
          "flex items-center gap-4 px-5 py-4 cursor-pointer transition-all duration-200",
          !isExpanded && "hover:bg-gray-50/30"
        )}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* AI indicator - Dark indigo gradient */}
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)' }}
        >
          <Sparkles className="w-4.5 h-4.5 text-violet-300" />
        </div>
        
        {/* Title and Summary */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h2 className="text-sm font-semibold text-gray-900">
              {i18n.language === 'nb' ? 'Saksoversikt' : 'Case Summary'}
            </h2>
            {hasCached && !isRefreshing && (
              <span className="px-1.5 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-600 rounded">
                {i18n.language === 'nb' ? 'AI-generert' : 'AI-powered'}
              </span>
            )}
          </div>
          {isRefreshing ? (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>{i18n.language === 'nb' ? 'Analyserer sak...' : 'Analyzing case...'}</span>
            </div>
          ) : (
            <p className="text-xs text-gray-500 truncate">
              {!isExpanded && (parsedBrief?.summary?.slice(0, 80) || i18n.language === 'nb' ? 'Klikk for å utvide' : 'Click to expand')}
              {isExpanded && (i18n.language === 'nb' ? 'AI-drevet saksintelligens' : 'AI-powered case intelligence')}
            </p>
          )}
        </div>

        {/* Quick stats when collapsed */}
        {!isExpanded && parsedBrief && (
          <div className="hidden md:flex items-center gap-3 text-xs">
            {parsedBrief.keyPoints?.length > 0 && (
              <span className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 rounded-full text-gray-600">
                <Lightbulb className="w-3 h-3" />
                {parsedBrief.keyPoints.length}
              </span>
            )}
            {parsedBrief.risks?.length > 0 && (
              <span className="flex items-center gap-1.5 px-2 py-1 bg-amber-50 rounded-full text-amber-600">
                <AlertTriangle className="w-3 h-3" />
                {parsedBrief.risks.length}
              </span>
            )}
            {parsedBrief.nextSteps?.length > 0 && (
              <span className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 rounded-full text-blue-600">
                <Target className="w-3 h-3" />
                {parsedBrief.nextSteps.length}
              </span>
            )}
          </div>
        )}

        {/* Controls */}
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); generateNewBrief(); }}
            disabled={isRefreshing}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all disabled:opacity-50"
            title={i18n.language === 'nb' ? 'Oppdater analyse' : 'Refresh analysis'}
          >
            <RefreshCw className={cn("w-4 h-4", isRefreshing && "animate-spin")} />
          </button>
          <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all">
            <ChevronDown className={cn(
              "w-4 h-4 transition-transform duration-300",
              isExpanded && "rotate-180"
            )} />
          </button>
        </div>
      </div>

      {/* === EXPANDED CONTENT === */}
      <div className={cn(
        "transition-all duration-500 ease-out",
        isExpanded ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
      )}>
        {brief && (
          <div className="px-5 pb-5">
            {/* Divider */}
            <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent mb-5" />
            
            {/* Main content - 2 column layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Left: Summary & Key Points */}
              <div className="lg:col-span-2 space-y-5">
                {/* Summary - clean, no duplicate header */}
                <p className="text-sm text-gray-700 leading-relaxed">
                  {parsedBrief?.summary || brief?.content?.split('\n')[0]?.replace(/\*\*/g, '')}
                </p>

                {/* Key Points */}
                {parsedBrief?.keyPoints?.length > 0 && (
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <Lightbulb className="w-4 h-4 text-amber-500" />
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        {i18n.language === 'nb' ? 'Hovedpunkter' : 'Key Points'}
                      </h3>
                    </div>
                    <div className="space-y-2">
                      {parsedBrief.keyPoints.slice(0, 4).map((point, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 bg-gray-50/50 rounded-lg hover:bg-gray-50 transition-colors">
                          <span className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600 flex-shrink-0 mt-0.5">
                            {i + 1}
                          </span>
                          <span className="text-sm text-gray-700">{point}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Risks */}
                {(parsedBrief?.risks?.length > 0 || brief?.red_flags?.length > 0) && (
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <Shield className="w-4 h-4 text-amber-500" />
                      <h3 className="text-xs font-semibold text-amber-600 uppercase tracking-wider">
                        {i18n.language === 'nb' ? 'Risikoområder' : 'Risk Areas'}
                      </h3>
                    </div>
                    <div className="bg-gradient-to-br from-amber-50 to-orange-50/50 border border-amber-200/50 rounded-xl p-4">
                      <div className="space-y-2">
                        {(parsedBrief?.risks || brief?.red_flags || []).slice(0, 3).map((risk, i) => (
                          <div key={i} className="flex items-start gap-3 text-sm">
                            <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                            <span className="text-amber-900">{typeof risk === 'string' ? risk : risk.issue}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>
                )}
              </div>

              {/* Right: Actions */}
              <div className="space-y-4">
                {/* Primary document action */}
                {primaryDocument && (
                  <button
                    onClick={handleDownloadDocument}
                    className="w-full p-4 bg-gradient-to-br from-gray-900 to-gray-800 hover:from-gray-800 hover:to-gray-700 text-white rounded-xl transition-all group shadow-lg shadow-gray-900/10"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                        <FileText className="w-5 h-5" />
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <div className="text-sm font-medium">{i18n.language === 'nb' ? 'Åpne dokument' : 'Open Document'}</div>
                        <div className="text-xs text-gray-400 truncate">{primaryDocument.filename}</div>
                      </div>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>
                )}

                {/* Next Steps */}
                {parsedBrief?.nextSteps?.length > 0 && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Target className="w-3.5 h-3.5 text-blue-500" />
                      {i18n.language === 'nb' ? 'Neste steg' : 'Next Steps'}
                    </h4>
                    <div className="space-y-2">
                      {parsedBrief.nextSteps.slice(0, 3).map((step, i) => (
                        <div 
                          key={i}
                          className="flex items-center gap-3 p-2.5 bg-white hover:bg-blue-50 rounded-lg cursor-pointer transition-all group border border-gray-100 hover:border-blue-200"
                        >
                          <div className="w-5 h-5 rounded-full border-2 border-gray-300 group-hover:border-blue-400 group-hover:bg-blue-400 flex-shrink-0 flex items-center justify-center transition-all">
                            <CheckCircle className="w-3 h-3 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          <span className="text-sm text-gray-700 group-hover:text-gray-900 flex-1 truncate">{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick actions */}
                <div className="space-y-2">
                  <button className="w-full flex items-center gap-3 p-3 text-sm text-gray-600 hover:text-gray-900 bg-white hover:bg-gray-50 rounded-xl transition-all border border-gray-100 hover:border-gray-200">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span>{i18n.language === 'nb' ? 'Send e-post til klient' : 'Draft email to client'}</span>
                  </button>
                  <button className="w-full flex items-center gap-3 p-3 text-sm text-gray-600 hover:text-gray-900 bg-white hover:bg-gray-50 rounded-xl transition-all border border-gray-100 hover:border-gray-200">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>{i18n.language === 'nb' ? 'Sett påminnelse' : 'Set reminder'}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between pt-4 mt-5 border-t border-gray-100 text-xs text-gray-400">
              <span className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                {brief.generated_at && formatDistanceToNow(new Date(brief.generated_at), { addSuffix: true, locale: dateLocale })}
              </span>
              <span className="flex items-center gap-1.5">
                <Zap className="w-3 h-3" />
                {i18n.language === 'nb' ? 'Drevet av AI' : 'Powered by AI'}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StickyBrief;
